import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { syncHelpGuides, type IHelpSyncResult } from '@apis/help';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { Menu } from '@components/ui/Menu/Menu';
import { useCanAccess } from '@permissions/useFeatures';
import { helpRootKey } from '@constants/queryKeys';
import styles from './HelpSettingsMenu.module.css';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

const RESULT_VISIBLE_MS = 5000;

/** The hub's settings menu — Trusstor admins only. "Update" pulls the latest guides
 *  now; nothing else syncs them. Progress and the result show beside the menu. */
export function HelpSettingsMenu() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isAllowed = useCanAccess('admin.trusstor');
  const [outcome, setOutcome] = useState<IHelpSyncResult | 'failed' | null>(null);
  const sync = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: syncHelpGuides,
    onSuccess: (result) => {
      setOutcome(result);
      void queryClient.invalidateQueries({ queryKey: helpRootKey });
    },
    onError: () => setOutcome('failed'),
  });

  useEffect(() => {
    if (outcome === null) return;
    const timer = setTimeout(() => setOutcome(null), RESULT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [outcome]);

  if (!isAllowed) return null;
  const status = sync.isPending
    ? t('help.updating')
    : outcome === 'failed'
      ? t('help.updateFailed')
      : outcome
        ? t('help.syncResult', { fetched: outcome.fetched, removed: outcome.removed })
        : null;
  return (
    <div className={styles.settings}>
      <span role="status" className={outcome === 'failed' ? styles.failed : styles.result}>{status}</span>
      <Menu.Root>
        <Menu.Trigger render={<KebabButton aria-label={t('help.settings')} />} />
        <Menu.Portal>
          <Menu.Positioner side="bottom" align="end" sideOffset={4}>
            <Menu.Popup>
              <Menu.Item disabled={sync.isPending} onClick={() => { setOutcome(null); sync.mutate(); }}>
                {t('help.update')}
              </Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}
